import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {trips} from "../data/trips";

const IDLE_MS = 1200;     // how long the bubble (and the whole scrubber on mobile) lingers after scrolling
const SNAP_PX = 8;        // how close the pointer needs to be to a tick to hover/click it
const PROBE = 0.3;        // a trip is "current" once its card passes 30% down the viewport
const RELAYOUT_MS = 600;  // how long the thumb animates after the page height changes
const SETTLE_MS = 800;    // re-measure once a new page's entrance animation has finished
const QUIET_MS = 200;     // scrolls this soon after a page change are the jump to the top, not the visitor

const pct = (f: number) => `${f * 100}%`;
const yearOf = (date: string) => date.split(" ")[1];

// The site's scrollbar (it replaces the browser's), mounted once in Block so it
// carries over between pages and fades instead of vanishing. On the photos page
// (`trips`) it's a timeline: ticks mark where each trip card sits in the scroll
// area (as a fraction of its height), the thumb mirrors the visible window, and
// a glass bubble names the current trip while scrolling. Elsewhere it's just the
// thumb, shown while scrolling. On mobile, CSS reduces the photos version to
// the thumb + date bubble, also shown only while scrolling.
export function ScrollTimeline({trips: tripsMode, route}: {trips: boolean, route: string}) {
    const rootRef = useRef<HTMLDivElement>(null);
    const railRef = useRef<HTMLDivElement>(null);
    const marksRef = useRef<number[]>([]);
    const idleRef = useRef<number>();
    const relayoutRef = useRef<number>();
    const dragRef = useRef<{pointerId: number; startY: number; moved: boolean} | null>(null);
    const modeRef = useRef(tripsMode);
    modeRef.current = tripsMode;
    const quietUntil = useRef(0);
    const settleRef = useRef<number>();
    // The thumb and bubble positions change on every scroll, so they're kept
    // out of React state and written straight to the DOM (paint); re-rendering
    // the timeline on each scroll event was the main cost on slow devices.
    const viewRef = useRef({top: 0, height: 1});
    const thumbRef = useRef<HTMLDivElement>(null);
    const bubbleRef = useRef<HTMLDivElement>(null);
    const hoverRef = useRef<number | null>(null);

    const [marks, setMarks] = useState<number[]>([]);
    const [scrollable, setScrollable] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);
    const [scrolling, setScrolling] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [hoverIdx, setHoverIdx] = useState<number | null>(null);
    const [relayout, setRelayout] = useState(false);

    // let the thumb glide to its new size/position for a moment (it otherwise tracks scroll 1:1)
    const animateRelayout = useCallback(() => {
        setRelayout(true);
        window.clearTimeout(relayoutRef.current);
        relayoutRef.current = window.setTimeout(() => setRelayout(false), RELAYOUT_MS);
    }, []);

    const paint = useCallback(() => {
        const {top, height} = viewRef.current;
        if (thumbRef.current) {
            thumbRef.current.style.top = pct(top);
            thumbRef.current.style.height = pct(height);
        }
        if (bubbleRef.current) {
            const hover = hoverRef.current;
            bubbleRef.current.style.top = pct(hover !== null ? marksRef.current[hover] ?? 0 : top + height / 2);
        }
    }, []);

    const getScroller = () => rootRef.current?.closest<HTMLElement>(".vignette") ?? null;

    const update = useCallback(() => {
        const v = getScroller();
        if (!v) return;
        const total = v.scrollHeight;
        const canScroll = total > v.clientHeight + 1;
        // nothing to scroll: leave the thumb where it was while the scrollbar fades out
        if (canScroll) viewRef.current = {top: v.scrollTop / total, height: v.clientHeight / total};
        setScrollable(canScroll); // these two only re-render when they actually change
        const probe = (v.scrollTop + v.clientHeight * PROBE) / total;
        let idx = 0;
        marksRef.current.forEach((m, i) => { if (m <= probe) idx = i; });
        setActiveIdx(idx);
        paint();
    }, [paint]);

    // after any render (hover, a new active trip...), put the thumb and bubble back in place
    useLayoutEffect(() => {
        hoverRef.current = hoverIdx;
        paint();
    });

    const measure = useCallback(() => {
        const v = getScroller();
        if (!v) return;
        // off the photos page, keep the last marks so they can fade out with it
        if (!modeRef.current) return update();
        const vTop = v.getBoundingClientRect().top;
        const cards = Array.from(document.querySelectorAll<HTMLElement>("#photos .trip-card"));
        marksRef.current = cards.map(c => (c.getBoundingClientRect().top - vTop + v.scrollTop) / v.scrollHeight);
        setMarks(marksRef.current);
        update();
    }, [update]);

    useEffect(() => {
        const v = getScroller();
        const page = rootRef.current?.closest<HTMLElement>(".block");
        if (!v || !page) return;
        measure();
        // the page height changes (expanding/collapsing trips, a new page), so
        // re-measure; briefly let the thumb animate too (it otherwise tracks scroll 1:1)
        const ro = new ResizeObserver(() => {
            animateRelayout();
            measure();
        });
        ro.observe(page);
        const onScroll = () => {
            update();
            if (performance.now() < quietUntil.current) return;
            setScrolling(true);
            window.clearTimeout(idleRef.current);
            idleRef.current = window.setTimeout(() => setScrolling(false), IDLE_MS);
        };
        v.addEventListener("scroll", onScroll, {passive: true});
        window.addEventListener("resize", measure);
        return () => {
            ro.disconnect();
            v.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", measure);
            window.clearTimeout(idleRef.current);
            window.clearTimeout(relayoutRef.current);
        };
    }, [measure, update, animateRelayout]);

    // new page: don't flash up for the jump back to the top, and measure the
    // new page now and again once its content has finished animating in
    useEffect(() => {
        quietUntil.current = performance.now() + QUIET_MS;
        setScrolling(false);
        setHoverIdx(null);
        animateRelayout();
        measure();
        window.clearTimeout(settleRef.current);
        settleRef.current = window.setTimeout(measure, SETTLE_MS);
        return () => window.clearTimeout(settleRef.current);
    }, [route, tripsMode, measure, animateRelayout]);

    const railFraction = (clientY: number) => {
        const rect = railRef.current!.getBoundingClientRect();
        return Math.min(1, Math.max(0, (clientY - rect.top) / rect.height));
    };
    const nearestTick = (clientY: number) => {
        if (!modeRef.current) return null;
        const rect = railRef.current!.getBoundingClientRect();
        let best: number | null = null;
        let bestDist = SNAP_PX;
        marksRef.current.forEach((m, i) => {
            const d = Math.abs(rect.top + m * rect.height - clientY);
            if (d <= bestDist) { best = i; bestDist = d; }
        });
        return best;
    };
    const scrollToFraction = (f: number, behavior: ScrollBehavior) => {
        const v = getScroller();
        if (v) v.scrollTo({top: f * v.scrollHeight - v.clientHeight / 2, behavior});
    };

    const onPointerDown = (e: React.PointerEvent) => {
        e.stopPropagation();
        e.preventDefault(); // don't start a text selection while scrubbing
        railRef.current!.setPointerCapture(e.pointerId);
        dragRef.current = {pointerId: e.pointerId, startY: e.clientY, moved: false};
    };
    const onPointerMove = (e: React.PointerEvent) => {
        const drag = dragRef.current;
        if (drag && drag.pointerId === e.pointerId) {
            if (!drag.moved && Math.abs(e.clientY - drag.startY) > 3) {
                drag.moved = true;
                setDragging(true);
                setHoverIdx(null);
            }
            if (drag.moved) scrollToFraction(railFraction(e.clientY), "auto");
        } else if (e.pointerType === "mouse") {
            setHoverIdx(nearestTick(e.clientY));
        }
    };
    const onPointerUp = (e: React.PointerEvent) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== e.pointerId) return;
        dragRef.current = null;
        setDragging(false);
        if (drag.moved) return;
        // a click: jump to the trip under the pointer, or to that point in the page
        const idx = nearestTick(e.clientY);
        const v = getScroller();
        if (idx !== null && v) v.scrollTo({top: marksRef.current[idx] * v.scrollHeight, behavior: "smooth"});
        else scrollToFraction(railFraction(e.clientY), "smooth");
    };

    const shownIdx = hoverIdx ?? activeIdx;
    const shown = trips[shownIdx];
    const bubbleOn = tripsMode && (hoverIdx !== null || scrolling || dragging);
    const activeYear = yearOf(trips[activeIdx]?.date ?? "");
    const yearStarts = trips
        .map((t, i) => ({year: yearOf(t.date), i}))
        .filter(({year, i}) => i === 0 || year !== yearOf(trips[i - 1].date));

    return (
        <div
            ref={rootRef}
            className={`timeline${tripsMode ? " trips" : ""}${scrollable ? "" : " no-scroll"}${scrolling || dragging ? " scrolling" : ""}${bubbleOn ? " bubble-on" : ""}${relayout ? " relayout" : ""}`}
            onClick={(e) => e.stopPropagation()}
        >
            <div
                ref={railRef}
                className="timeline-rail"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={() => setHoverIdx(null)}
            >
                <div className="timeline-line"/>
                <div className="timeline-marks">
                    {marks.map((m, i) => (
                        <div key={trips[i]?.id ?? i} className={`timeline-tick${i === activeIdx ? " active" : ""}`} style={{top: pct(m)}}/>
                    ))}
                    {yearStarts.map(({year, i}) => marks[i] !== undefined && (
                        <div key={year} className={`timeline-year${year === activeYear ? " active" : ""}`} style={{top: pct(marks[i])}}>
                            {year}
                        </div>
                    ))}
                </div>
                <div ref={thumbRef} className="timeline-thumb"/>
                {shown && (
                    <div ref={bubbleRef} className="timeline-bubble">
                        <span className="timeline-bubble-date">{shown.date}</span>
                        <span className="timeline-bubble-title">{shown.title}</span>
                    </div>
                )}
            </div>
        </div>
    );
}
